import { useState, useCallback, useEffect } from 'react';

import {
  useGetPendingPortfolioInvitationQuery,
  useCompletePortfolioOnboardingMutation,
  CompletePortfolioOnboardingResponse,
} from '@client/api';

import { BrregCompanyData } from '../../companies/hooks/useBrregCompanySearch';

import { OnboardingLayout } from './onboarding-layout';
import {
  OnboardingState,
  OnboardingBuilding,
  OnboardingUser,
  ImportCompanyCandidate,
} from './onboarding.types';
import { StepSelskap } from './steps/step-selskap';
import { StepBrukere } from './steps/step-brukere';
import { StepBygg } from '../buildings/wizard/step-bygg';
import { StepRoller } from '../buildings/wizard/step-roller';
import { StepImporter } from '../buildings/wizard/step-importer';
import { StepGjennomgang } from './steps/step-gjennomgang';
import { StepFerdig } from './steps/step-ferdig';

const INITIAL_STATE: OnboardingState = {
  currentStep: 0,
  company: null,
  users: [],
  buildings: [],
  importCompanies: [],
};

const STORAGE_KEY = 'exio.onboarding';

// Step indices (must stay aligned with ONBOARDING_STEPS order):
// 0 = selskap, 1 = brukere, 2 = bygg, 3 = roller, 4 = importer, 5 = gjennomgang, 6 = ferdig
const STEP_FERDIG = 6;

function loadSavedState(): OnboardingState {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as OnboardingState;
      // Don't restore at or past the gjennomgang step (5) — the submit step needs a fresh submit
      if (parsed.currentStep >= STEP_FERDIG) return INITIAL_STATE;
      // Backfill importCompanies for old saved states
      return { ...INITIAL_STATE, ...parsed, importCompanies: parsed.importCompanies ?? [] };
    }
  } catch {
    // corrupt data — start fresh
  }
  return INITIAL_STATE;
}

export function OnboardingWizard() {
  const [state, setState] = useState<OnboardingState>(loadSavedState);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(!loadSavedState().company);
  const [result, setResult] = useState<CompletePortfolioOnboardingResponse | null>(null);

  const { data: invitation, isLoading: isLoadingInvitation, isSuccess: isInvitationLoaded } = useGetPendingPortfolioInvitationQuery();
  const [completeOnboarding, { isLoading: isSubmitting }] = useCompletePortfolioOnboardingMutation();

  // Reset saved state if the invitation company changed (new invitation for a different company)
  useEffect(() => {
    if (invitation?.companyOrgNr && state.company && state.company.organisasjonsnummer !== invitation.companyOrgNr) {
      setState(INITIAL_STATE);
      // Keep the spinner up until the prefill effect (below) fetches the new
      // company from BRREG — otherwise StepSelskap briefly renders "Ingen
      // selskapsdata funnet fra invitasjonen" between the reset and refetch.
      setIsLoadingCompany(true);
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [invitation?.companyOrgNr, state.company]);

  // Pre-fill company from invitation — fetch full details from Brreg
  // Only act once the invitation query has finished loading.
  // NOTE: `state.company?.organisasjonsnummer` is in the deps so this effect
  // re-runs after the reset effect above clears `state.company`; without it,
  // a fresh invitation for a different org would leave `state.company` null
  // and never retrigger the BRREG fetch until a hard reload.
  useEffect(() => {
    if (isLoadingInvitation) return;

    if (invitation?.companyOrgNr && !state.company) {
      setIsLoadingCompany(true);
      fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${invitation.companyOrgNr}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data: BrregCompanyData | null) => {
          if (data) {
            setState((prev) => (prev.company ? prev : { ...prev, company: data }));
          } else if (invitation.companyName) {
            setState((prev) =>
              prev.company
                ? prev
                : {
                    ...prev,
                    company: {
                      organisasjonsnummer: invitation.companyOrgNr!,
                      navn: invitation.companyName!,
                    } as BrregCompanyData,
                  },
            );
          }
        })
        .catch(() => {
          if (invitation.companyName) {
            setState((prev) =>
              prev.company
                ? prev
                : {
                    ...prev,
                    company: {
                      organisasjonsnummer: invitation.companyOrgNr!,
                      navn: invitation.companyName!,
                    } as BrregCompanyData,
                  },
            );
          }
        })
        .finally(() => setIsLoadingCompany(false));
    } else {
      setIsLoadingCompany(false);
    }
  }, [invitation?.companyOrgNr, isLoadingInvitation, state.company?.organisasjonsnummer]);

  // Persist state to localStorage on every change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // storage full or unavailable — ignore
    }
  }, [state]);

  const goNext = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, STEP_FERDIG) }));
  }, []);

  const goBack = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, 0) }));
  }, []);

  const setUsers = useCallback((users: OnboardingUser[]) => {
    setState((prev) => ({ ...prev, users }));
  }, []);

  const setBuildings = useCallback((buildings: OnboardingBuilding[]) => {
    setState((prev) => ({ ...prev, buildings }));
  }, []);

  const setImportCompanies = useCallback((importCompanies: ImportCompanyCandidate[]) => {
    setState((prev) => ({ ...prev, importCompanies }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!state.company) return;
    setSubmitError(null);

    const selectedBuildings = state.buildings.filter((b) => b.selected);

    const selectedImports = state.importCompanies.filter(
      (c) => c.selected && c.orgNumber !== state.company?.organisasjonsnummer,
    );

    try {
      const res = await completeOnboarding({
        body: {
          company: {
            orgNr: state.company.organisasjonsnummer,
            name: state.company.navn,
            // Join all address lines to preserve full address when BRREG splits across lines (e.g. "c/o ...").
            address: state.company.forretningsadresse?.adresse?.length
              ? state.company.forretningsadresse.adresse.filter(Boolean).join(', ')
              : null,
            postalCode: state.company.forretningsadresse?.postnummer ?? null,
            city: state.company.forretningsadresse?.poststed ?? null,
            municipality: state.company.forretningsadresse?.kommune ?? null,
            organizationForm: state.company.organisasjonsform
              ? `${state.company.organisasjonsform.kode} - ${state.company.organisasjonsform.beskrivelse}`
              : null,
            industryCode: state.company.naeringskode1
              ? `${state.company.naeringskode1.kode} - ${state.company.naeringskode1.beskrivelse}`
              : null,
            institutionalSectorCode: state.company.institusjonellSektorkode
              ? `${state.company.institusjonellSektorkode.kode} - ${state.company.institusjonellSektorkode.beskrivelse}`
              : null,
            numberOfEmployees: state.company.antallAnsatte ?? null,
            isBankrupt: state.company.konkurs ?? null,
            isUnderLiquidation: state.company.underAvvikling ?? null,
            isUnderForcedDissolution: state.company.underTvangsavviklingEllerTvangsopplosning ?? null,
            parentOrganizationNumber: state.company.overordnetEnhet ?? null,
            registeredInVatRegistry: state.company.registrertIMvaregisteret ?? null,
            vatRegistrationDate: state.company.registreringsdatoMerverdiavgiftsregisteret ?? null,
            lastAnnualAccountsYear: state.company.sisteInnsendteAarsregnskap ?? null,
            activity: state.company.aktivitet?.length ? state.company.aktivitet.join(' ') : null,
            remarks: null,
            brregUrl: state.company._links?.self?.href ?? null,
            website: state.company.hjemmeside ?? null,
            emailAddress: state.company.epostadresse ?? null,
            mobilePhone: state.company.mobil ?? state.company.telefon ?? null,
            entityRegistrationDate: state.company.registreringsdatoEnhetsregisteret ?? null,
            isPartOfGroup: state.company.erIKonsern ?? null,
            languageCode: state.company.maalform ?? null,
          },
          buildings: selectedBuildings.map((b) => ({
            name: b.name ?? b.address,
            address: b.address,
            postalCode: b.postalCode,
            city: b.city,
            countryThreeLetterCode: b.countryThreeLetterCode ?? null,
            streetName: b.streetName ?? null,
            streetNumber: b.streetNumber ?? null,
            source: b.source ?? null,
            type: b.type,
            bra: b.bra,
            role: b.role,
            numberOfFloors: b.numberOfFloors ?? null,
            buildingNumber: b.buildingNumber ?? null,
            energySourceType: b.energySourceType ?? null,
            heatingType: b.heatingType ?? null,
            matrikkelBuildingTypeCode: b.matrikkelBuildingTypeCode ?? null,
            latitude: b.latitude ?? null,
            longitude: b.longitude ?? null,
            gnr: b.gnr ?? null,
            bnr: b.bnr ?? null,
            municipalityCode: b.municipalityCode ?? null,
            ownerOrgNumber: b.ownerOrgNumber ?? null,
            ownerName: b.ownerName ?? null,
            owners: b.owners?.map((o) => ({
              name: o.name ?? null,
              organizationNumber: o.organizationNumber ?? null,
              isJuridical: o.isJuridical,
              shareNumerator: o.shareNumerator ?? null,
              shareDenominator: o.shareDenominator ?? null,
            })) ?? null,
            ownerPath: b.ownerPath ?? null,
            ownerPathRootOrgNumber: b.ownerPathRootOrgNumber ?? null,
            energyLabel: b.energyLabel ?? null,
            heatingGrade: b.heatingGrade ?? null,
            constructionYear: b.constructionYear ?? null,
            energyCertificateUrl: b.energyCertificateUrl ?? null,
            energyCertificateNumber: b.energyCertificateNumber ?? null,
            energyCertificateIssuedDate: b.energyCertificateIssuedDate ?? null,
            isGrouped: b.isGrouped ?? false,
            childBuildings: b.isGrouped && b.childBuildings
              ? b.childBuildings.map((c) => ({
                  buildingNumber: c.buildingNumber ?? null,
                  bra: c.bra,
                  numberOfFloors: c.numberOfFloors ?? null,
                  matrikkelBuildingTypeCode: c.matrikkelBuildingTypeCode ?? null,
                  energyLabel: c.energyLabel ?? null,
                  heatingGrade: c.heatingGrade ?? null,
                  constructionYear: c.constructionYear ?? null,
                  energyCertificateUrl: c.energyCertificateUrl ?? null,
                  energyCertificateNumber: c.energyCertificateNumber ?? null,
                  energyCertificateIssuedDate: c.energyCertificateIssuedDate ?? null,
                }))
              : null,
          })),
          users: state.users.map((u) => ({
            email: u.email,
            role: u.role,
          })),
          // Ship only { orgNr, name } — the BE re-fetches every other BRREG field
          // server-side via BrregCompanyClient.GetByOrgNumberAsync, so data is
          // server-authoritative (no staleness, no FE tampering) and the request stays
          // tiny even when the admin ticks many companies.
          importCompanies: selectedImports.map((c) => ({
            orgNr: c.orgNumber,
            name: c.name,
          })),
        },
      }).unwrap();

      setResult(res);
      window.localStorage.removeItem(STORAGE_KEY);
      goNext();
    } catch (err: any) {
      setSubmitError(err?.data?.detail ?? err?.message ?? 'En feil oppstod. Prøv igjen.');
    }
  }, [state, completeOnboarding, goNext]);

  const renderStep = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <StepSelskap
            company={state.company}
            isLoading={isLoadingInvitation || isLoadingCompany}
            onNext={goNext}
          />
        );
      case 1:
        return (
          <StepBrukere
            users={state.users}
            onUsersChange={setUsers}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case 2:
        return (
          <StepBygg
            company={state.company}
            buildings={state.buildings}
            onBuildingsChange={setBuildings}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case 3:
        return (
          <StepRoller
            buildings={state.buildings}
            onBuildingsChange={setBuildings}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case 4:
        return (
          <StepImporter
            buildings={state.buildings}
            importCompanies={state.importCompanies}
            selfOrgNr={state.company?.organisasjonsnummer ?? null}
            onImportCompaniesChange={setImportCompanies}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case 5:
        return (
          <StepGjennomgang
            company={state.company}
            users={state.users}
            buildings={state.buildings}
            importCompanies={state.importCompanies}
            onBack={goBack}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      case 6:
        return <StepFerdig buildings={state.buildings} tenantId={result?.tenantId} tenantName={result?.tenantName} />;
      default:
        return null;
    }
  };

  if (isLoadingInvitation) {
    return (
      <OnboardingLayout currentStep={0}>
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-white/50">Laster...</p>
        </div>
      </OnboardingLayout>
    );
  }

  if (isInvitationLoaded && !invitation && !result) {
    return (
      <OnboardingLayout currentStep={0}>
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-4">Ingen invitasjon funnet</h2>
          <p className="text-white/60 max-w-md">
            Du er dessverre ikke invitert til å bruke tjenesten Exio Portefølje.
            Vennligst ta kontakt med{' '}
            <a href="mailto:support@exio.no" className="text-brand-primary hover:underline">
              support@exio.no
            </a>{' '}
            for tilgang.
          </p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep={state.currentStep}>
      {renderStep()}
    </OnboardingLayout>
  );
}
